import React, { useState, useEffect } from 'react';
import { FiShoppingBag, FiMenu, FiX, FiSearch } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = ({ cartCount, onOpenCart }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Arsenal', href: '#shop' },
    { name: 'Kit Builder', href: '#builder' },
    { name: 'Specs', href: '#compare' },
    { name: 'Intel', href: '#faq' },
  ];

  return (
    <nav className={`fixed w-full z-[100] transition-all duration-500 ${
      isScrolled ? 'py-3 glass-morphism shadow-2xl' : 'py-6 bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <a href="#" className="flex items-center gap-3 group" aria-label="BikeBloom Home">
          <div className="w-10 h-10 bg-signal flex items-center justify-center transform group-hover:rotate-[90deg] transition-transform duration-500 rounded-sm">
            <span className="text-white font-black text-2xl">B</span>
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-white font-black text-xl tracking-tighter uppercase">BikeBloom</span>
            <span className="text-[10px] text-gray-400 tracking-[0.3em] font-bold uppercase">Urban Systems</span>
          </div>
        </a>

        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            <a key={link.name} href={link.href} className="text-[11px] font-black text-gray-400 hover:text-signal uppercase tracking-[0.2em] transition-all relative group">
              {link.name}
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-signal transition-all group-hover:w-full"></span>
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4 md:gap-8">
          <button className="hidden sm:block text-gray-400 hover:text-white transition-colors" aria-label="Search gear">
            <SafeIcon icon={FiSearch} className="text-xl" />
          </button>
          <button 
            onClick={onOpenCart}
            className="relative flex items-center gap-2 group outline-none focus:ring-2 focus:ring-signal focus:ring-offset-2 focus:ring-offset-asphalt-dark rounded-sm p-1"
            aria-label={`View cart with ${cartCount} items`}
          >
            <div className="relative">
              <SafeIcon icon={FiShoppingBag} className="text-2xl text-white group-hover:text-signal transition-colors" />
              <AnimatePresence>
                {cartCount > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-1 -right-1 bg-signal text-white text-[9px] font-black w-4 h-4 flex items-center justify-center rounded-full border-2 border-asphalt-dark"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            <span className="hidden md:block text-[11px] font-black uppercase tracking-widest text-gray-400 group-hover:text-white transition-colors">Loadout</span>
          </button>
          <button className="lg:hidden text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            className="lg:hidden fixed inset-0 top-0 left-0 w-full h-screen bg-asphalt-dark z-[90] flex flex-col pt-32 px-10 gap-8"
          >
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                onClick={() => setMobileMenuOpen(false)}
                className="text-4xl font-black text-white hover:text-signal uppercase tracking-tighter transition-colors"
              >
                {link.name}
              </a>
            ))}
            <div className="mt-auto pb-20 flex gap-6">
              <div className="w-12 h-[2px] bg-signal"></div>
              <p className="text-gray-500 text-sm font-bold uppercase tracking-widest">Tactical Urban Experience</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;